import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useScrollLock } from '../../../_utils/useScrollLock';
import './PortfolioGalleryModal.css';

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const PortfolioGalleryModal = ({ isOpen, onClose, images, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  // Touch tracking: record start x/y, let the first meaningful move lock the
  // gesture to an axis, then apply one of two behaviours:
  //   - horizontal -> swipe to change image (existing behaviour)
  //   - vertical-down -> drag to dismiss, with live translateY feedback and
  //     a fading backdrop, matching the Apple Photos gesture
  const [touchStart, setTouchStart] = useState(null); // { x, y }
  const [touchEnd, setTouchEnd] = useState(null);     // clientX (horizontal only)
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [dragAxis, setDragAxis] = useState(null);     // 'horizontal' | 'vertical' | null
  const containerRef = useRef(null);

  // Minimum swipe distance to trigger navigation (in pixels)
  const minSwipeDistance = 50;
  // Downward drag distance past which the modal dismisses
  const dismissThreshold = 100;
  // Small deadband before locking the drag axis - stops jittery finger
  // movement from flipping intent between horizontal and vertical.
  const axisLockThreshold = 10;

  // Reset current index when modal opens with new initial index
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setDragOffsetX(0);
      setDragOffsetY(0);
      setDragAxis(null);
    }
  }, [isOpen, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length]);

  // Lock the page behind the modal so it can't scroll on iOS — shared
  // ref-counted lock so overlapping modals can't restore each other's
  // locked styles (see ConfirmBookingModal).
  useScrollLock(isOpen);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  // Touch handlers: direction-aware. Horizontal -> image nav. Vertical (down)
  // -> drag to dismiss with a live translateY and backdrop dim.
  const onTouchStart = (e) => {
    const t = e.targetTouches[0];
    setTouchStart({ x: t.clientX, y: t.clientY });
    setTouchEnd(null);
    setIsDragging(true);
    setDragAxis(null);
    setDragOffsetX(0);
    setDragOffsetY(0);
  };

  const onTouchMove = (e) => {
    if (!touchStart) return;
    const t = e.targetTouches[0];
    const dx = t.clientX - touchStart.x;
    const dy = t.clientY - touchStart.y;

    // First move past the deadband picks the axis and sticks with it so
    // horizontal jitter mid-dismiss can't flip us back to image-nav mode.
    let axis = dragAxis;
    if (!axis && (Math.abs(dx) > axisLockThreshold || Math.abs(dy) > axisLockThreshold)) {
      axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      setDragAxis(axis);
    }

    if (axis === 'horizontal') {
      setTouchEnd(t.clientX);
      setDragOffsetX(dx);
    } else if (axis === 'vertical') {
      // Only allow downward drag for dismiss. Upward drags stay pinned.
      setDragOffsetY(Math.max(0, dy));
    }
  };

  const onTouchEnd = () => {
    setIsDragging(false);

    if (dragAxis === 'vertical') {
      if (dragOffsetY > dismissThreshold) {
        onClose();
      }
      // else snap back (setting offsets below animates it via CSS transition)
    } else if (dragAxis === 'horizontal' && touchStart && touchEnd !== null) {
      const distance = touchStart.x - touchEnd;
      if (distance > minSwipeDistance) {
        goToNext();
      } else if (distance < -minSwipeDistance) {
        goToPrevious();
      }
    }

    setDragOffsetX(0);
    setDragOffsetY(0);
    setDragAxis(null);
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Mouse handlers for desktop drag. Horizontal only - desktop users close
  // via the X button or ESC, not a pull-down gesture.
  const onMouseDown = (e) => {
    e.preventDefault();
    setTouchStart({ x: e.clientX, y: e.clientY });
    setTouchEnd(null);
    setIsDragging(true);
    setDragAxis('horizontal');
  };

  const onMouseMove = (e) => {
    if (!isDragging || !touchStart) return;
    setTouchEnd(e.clientX);
    setDragOffsetX(e.clientX - touchStart.x);
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (touchStart && touchEnd !== null) {
      const distance = touchStart.x - touchEnd;
      if (distance > minSwipeDistance) {
        goToNext();
      } else if (distance < -minSwipeDistance) {
        goToPrevious();
      }
    }

    setDragOffsetX(0);
    setDragOffsetY(0);
    setDragAxis(null);
    setTouchStart(null);
    setTouchEnd(null);
  };

  const onMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setDragOffsetX(0);
      setDragOffsetY(0);
      setDragAxis(null);
      setTouchStart(null);
      setTouchEnd(null);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !images || images.length === 0) return null;

  // Fade the black backdrop as the user drags the modal down. Stops at 0.4
  // so the underlying page never becomes fully uncovered, which would look
  // jarring if the user released below-threshold and we snapped back.
  const overlayOpacityOverride =
    dragAxis === 'vertical' && dragOffsetY > 0
      ? Math.max(0.4, 1 - dragOffsetY / 500)
      : null;

  return (
    <div
      className={`portfolio-gallery-modal-overlay ${dragAxis === 'vertical' ? 'dragging-dismiss' : ''}`}
      onClick={handleBackdropClick}
      style={
        overlayOpacityOverride !== null
          ? { backgroundColor: `rgba(0, 0, 0, ${overlayOpacityOverride})` }
          : undefined
      }
    >
      <div
        className="portfolio-gallery-modal"
        style={{
          // Lift the whole modal contents during a vertical-dismiss drag.
          transform: dragOffsetY > 0 ? `translateY(${dragOffsetY}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {/* Close button */}
        <button className="gallery-modal-close" onClick={onClose}>
          <CloseIcon />
        </button>

        {/* Image counter */}
        <div className="gallery-modal-counter">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Navigation arrows (desktop) */}
        {images.length > 1 && (
          <>
            <button
              className="gallery-modal-nav gallery-modal-nav-prev"
              onClick={goToPrevious}
              aria-label="Previous image"
            >
              <ChevronLeftIcon />
            </button>
            <button
              className="gallery-modal-nav gallery-modal-nav-next"
              onClick={goToNext}
              aria-label="Next image"
            >
              <ChevronRightIcon />
            </button>
          </>
        )}

        {/* Image container with swipe support */}
        <div
          className="gallery-modal-image-container"
          ref={containerRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
        >
          <div
            className="gallery-modal-image-wrapper"
            style={{
              transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffsetX}px))`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out'
            }}
          >
            {images.map((image, index) => (
              <div key={index} className="gallery-modal-slide">
                <img
                  src={image}
                  alt={`Portfolio ${index + 1}`}
                  draggable="false"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Dot indicators */}
        {images.length > 1 && images.length <= 10 && (
          <div className="gallery-modal-dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={`gallery-modal-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioGalleryModal;
